// ─────────────────────────────────────────────────────────────────────────────
// GrievanceDetail — full read-only detail for a single grievance.
// Surfaces every field, including capture-only extras and the F585 notification
// state, organized to mirror the intake workflow sections.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { Card, Pill, NotifyMark, val, fmtDate, Missing } from "./reports/_shared";
import {
  CATEGORIES, STATUSES, categoryLabel, departmentLabel, statusLabel, complainantLabel,
  daysToClose,
} from "./grievanceModel";
import { printGrievance, downloadGrievanceDoc, grievanceContributors } from "./grievanceExport";

export default function GrievanceDetail({ record, onBack, facilityName, generatedBy, onNotice }) {
  if (!record) return null;
  const r = record;
  const st = STATUSES[r.status] || {};
  const d = daysToClose(r);
  const contributors = grievanceContributors(r);
  const exportOpts = { facilityName: facilityName || r.facilityName, generatedBy };
  // A popup blocker is the one failure here that is silent, so the message
  // comes back from the call rather than being assumed away.
  const doPrint = () => { const msg = printGrievance(r, exportOpts); if (msg) (onNotice || window.alert)(msg); };

  return (
    <div style={{ maxWidth: 920, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn-secondary" onClick={onBack} style={{ fontSize: 13 }}>← Back to Grievance Log</button>
        {/* The paper trail §483.10(j)(4)(iv) asks for. Both outputs are built
            from the same document, so the Word file and the printed copy
            cannot drift into saying different things about one grievance. */}
        <button className="btn-secondary" onClick={doPrint}
          title="Opens the print dialog — choose “Save as PDF” there for a PDF"
          style={{ marginLeft: "auto", fontSize: 13 }}>🖨 Print / PDF</button>
        <button className="btn-secondary" onClick={() => downloadGrievanceDoc(r, exportOpts)}
          title={contributors.length ? `Word document, authored to ${contributors[0].name}` : "Word document"}
          style={{ fontSize: 13 }}>⬇ Word</button>
      </div>

      {/* Header */}
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "var(--ed-ink)", margin: 0 }}>{val(r.residentName, { missing: r.complainantType !== "anonymous" })}</h2>
          <Pill label={categoryLabel(r.category)} color={CATEGORIES[r.category]?.color} bg={CATEGORIES[r.category]?.bg} />
          <Pill label={statusLabel(r.status)} color={st.color} bg={st.bg} border={st.border} />
          {r.createdLocally && <span className="badge" style={{ color: "var(--ed-violet-deep)", background: "var(--ed-violet-soft)", border: "1px solid var(--ed-violet-line)" }}>Locally created</span>}
          <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--ed-muted-2)" }}>{r.id}</span>
        </div>
        <div style={{ fontSize: 13, color: "var(--ed-muted)", marginTop: 6 }}>{r.facilityName} · CCN {r.facilityCcn}</div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14 }}>
        <Section title="Grievance Details">
          <Row label="Reported Date" value={fmtDate(r.dateReceived)} />
          <Row label="Incident Date" value={fmtDate(r.incidentDate)} missing />
          <Row label="Filed Date" value={fmtDate(r.filedDate)} missing />
          <Row label="Filed By" value={r.filedBy} missing />
          <Row label="Reported By" value={complainantLabel(r.complainantType)} />
          <Row label="Relation to Resident" value={r.relationToResident} missing />
          <Row label="Category" value={categoryLabel(r.category)} />
          <Row label="Department" value={departmentLabel(r.department)} />
          <Row label="Assigned To" value={r.assignedTo} missing />
        </Section>

        <Section title="Narrative">
          <Para text={r.narrative} />
          {(r.employeesMentioned || []).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={subLabel}>Employees Mentioned</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                {r.employeesMentioned.map((e) => <span key={e} className="badge" style={{ color: "#be123c", background: "#fff1f2", border: "1px solid #fecdd3" }}>{e}</span>)}
              </div>
            </div>
          )}
        </Section>

        <Section title="Follow Up">
          <div style={subLabel}>Investigation Notes</div>
          <Para text={r.investigationNotes} missing />
          <div style={{ ...subLabel, marginTop: 12 }}>Action Notes</div>
          {(r.actionNotes || []).length === 0 ? (
            <div style={{ fontSize: 13 }}><Missing /></div>
          ) : (
            r.actionNotes.map((a, i) => (
              <div key={i} style={{ fontSize: 13, color: "var(--ed-ink-2)", padding: "6px 0", borderBottom: "1px solid var(--ed-line-2)" }}>
                <span style={{ color: "var(--ed-muted-2)" }}>{a.date} · {a.addedBy}: </span>{a.note}
              </div>
            ))
          )}
        </Section>

        <Section title="Resolution">
          <Row label="Status" value={statusLabel(r.status)} />
          <Row label="Resolution Date" value={fmtDate(r.resolutionDate)} missing />
          <Row label="Days to Close" value={d == null ? null : `${d} day${d === 1 ? "" : "s"}`} missing />
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13.5 }}>
            <span style={{ color: "var(--ed-muted)" }}>Resident Notified (F585)</span>
            <span><NotifyMark sent={r.residentNotificationSent} /></span>
          </div>
          <Row label="Notification Date" value={fmtDate(r.residentNotificationDate)} missing />
          <div style={{ ...subLabel, marginTop: 10 }}>Outcome</div>
          <Para text={r.outcome} missing />
        </Section>

        {/* WHO PUT THIS RECORD TOGETHER — on screen, matching the export.
            The first name is the author: the person who created the record,
            and the name Word will show under File → Info. Employees NAMED IN
            the grievance are not here; they appear under the narrative, where
            being named is a claim and not a contribution. */}
        <Section title="Documented By">
          {contributors.length === 0 ? (
            <div style={{ fontSize: 13 }}><Missing title="No contributor recorded on this grievance" /></div>
          ) : (
            contributors.map((c, i) => (
              <div key={c.name} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", fontSize: 13.5, borderBottom: "1px solid var(--ed-bg)" }}>
                <span style={{ color: "var(--ed-ink)", fontWeight: 600 }}>
                  {c.name}
                  {i === 0 && <span className="badge" style={{ marginLeft: 7, color: "var(--ed-brand)", background: "var(--ed-brand-soft)", border: "1px solid var(--ed-info-line)" }}>Author</span>}
                </span>
                <span style={{ color: "var(--ed-muted)", textAlign: "right" }}>{c.roles.join(" · ")}</span>
              </div>
            ))
          )}
          <div style={{ fontSize: 11.5, color: "var(--ed-muted-2)", marginTop: 8, lineHeight: 1.5 }}>
            Carried into the printed and Word copies. The author is the person who created the record,
            not whoever exports it.
          </div>
        </Section>

        <Section title="Associated Files">
          {(r.attachments || []).length === 0 ? (
            <div style={{ fontSize: 13 }}><Missing /></div>
          ) : (
            r.attachments.map((a, i) => (
              <div key={i} style={{ fontSize: 13, color: "var(--ed-ink-2)", padding: "5px 0" }}>📎 {a.name || a}</div>
            ))
          )}
        </Section>
      </div>
    </div>
  );
}

const subLabel = { fontSize: 10.5, fontWeight: 700, color: "var(--ed-muted-2)", textTransform: "uppercase", letterSpacing: ".05em" };

function Section({ title, children }) {
  return (
    <Card>
      <div className="section-header">{title}</div>
      {children}
    </Card>
  );
}

function Row({ label, value, missing }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", fontSize: 13.5, borderBottom: "1px solid var(--ed-bg)" }}>
      <span style={{ color: "var(--ed-muted)" }}>{label}</span>
      <span style={{ color: "var(--ed-ink)", fontWeight: 600, textAlign: "right" }}>{val(value, { missing })}</span>
    </div>
  );
}

function Para({ text, missing }) {
  return <div style={{ fontSize: 13.5, color: "var(--ed-ink-2)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{val(text, { missing })}</div>;
}
